export interface MasonryTile {
  id: string
  left: number
  top: number
  width: number
  height: number
}

export interface MasonryLayoutOptions {
  containerWidth: number
  gap: number
}

export interface MasonryLayoutResult {
  tiles: Array<MasonryTile>
  totalHeight: number
}

interface MasonryItemInput {
  id: string
  width: number
  height: number
}

// Mirrors the legacy masonic grid: 2 columns below 900px, 3 above,
// items dropped into the currently shortest column, natural aspect ratios.
const columnCountFor = (containerWidth: number): number => (containerWidth < 900 ? 2 : 3)

export const computeMasonryLayout = (
  items: Array<MasonryItemInput>,
  { containerWidth, gap }: MasonryLayoutOptions
): MasonryLayoutResult => {
  if (containerWidth <= 0 || items.length === 0) {
    return { tiles: [], totalHeight: 0 }
  }

  const columns = columnCountFor(containerWidth)
  const columnWidth = Math.floor((containerWidth - gap * (columns - 1)) / columns)
  const columnHeights = new Array<number>(columns).fill(0)

  const tiles = items.map((item): MasonryTile => {
    const aspect = item.height / item.width
    const tileHeight = Math.max(1, Math.round(columnWidth * (Number.isFinite(aspect) && aspect > 0 ? aspect : 1)))

    let column = 0
    for (let i = 1; i < columns; i++) {
      if (columnHeights[i] < columnHeights[column]) column = i
    }

    const tile: MasonryTile = {
      id: item.id,
      left: column * (columnWidth + gap),
      top: columnHeights[column],
      width: columnWidth,
      height: tileHeight,
    }
    columnHeights[column] += tileHeight + gap
    return tile
  })

  return { tiles, totalHeight: Math.max(...columnHeights) - gap }
}
